import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getCurrentUserInfo, isRoleTypeAdmin } from 'src/reducers/users/currentUser';
import { REDUCER_NAME } from 'src/reducers/employees/employees';
import { getItem } from 'src/reducers/common/itemReducer';
import selector, { getOptionLabel, display, currency, decimalPlace2, numberWithComma } from 'src/utils/Utils';
import { getSelectOptions } from 'src/reducers/selectOptions';

import PayrollRuleInfo from './PayrollRuleInfo';
import tableStyles from '../styles/GeneralTableInfo.scss';

const EmployeeAllowances = ({ employeeAllowances, payrollRulesGroup }) => {
  if (_.isEmpty(employeeAllowances)) {
    return null;
  }
  const { t } = useTranslation();
  const isSemiMonthlyPayment = _.get(payrollRulesGroup, 'paymentCount') === 'twice';
  return (
    <React.Fragment>
      {_.map(employeeAllowances, item => (
        <tr key={item.id}>
          <th>{item.clientAllowanceName}：</th>
          <td>
            {isSemiMonthlyPayment ? (
              <>
                {t('features.Employees.components.PayrollRuleSectionShow.first_half')}:{' '}
                {currency(numberWithComma(decimalPlace2(item.allowanceAmount)))}
                <br />
                {t('features.Employees.components.PayrollRuleSectionShow.second_half')}:{' '}
                {currency(numberWithComma(decimalPlace2(item.paymentAmountSecondHalf)))}
              </>
            ) : (
              <>{currency(numberWithComma(decimalPlace2(item.paymentAmount)))}</>
            )}
          </td>
        </tr>
      ))}
    </React.Fragment>
  );
};

const fixedAmount = employeeUnitPrice => _.get(employeeUnitPrice, 'useType') !== 'payroll_rules_group';

const EmployeeUnitPrices = ({ employeeUnitPrices }) => {
  const { t } = useTranslation();
  if (_.isEmpty(employeeUnitPrices)) {
    return null;
  }
  return (
    <React.Fragment>
      {_.map(employeeUnitPrices, item => (
        <tr key={item.id}>
          <th>{item.clientUnitPriceName}：</th>
          <td>
            {fixedAmount(item)
              ? `${currency(numberWithComma(decimalPlace2(item.amount)))}`
              : t('features.Employees.components.PayrollRuleSectionShow.Follow_salary_rules')}
          </td>
        </tr>
      ))}
    </React.Fragment>
  );
};

const EmployeeMotivation = ({ isCalculateMotivation }) => {
  const { t } = useTranslation();
  return (
    <>
      <tr>
        <th>{t('features.Employees.components.PayrollRuleSectionShow.enable_calculate_motivation')}</th>
        <td>
          {isCalculateMotivation
            ? t('features.Employees.components.PayrollRuleSectionShow.yes')
            : t('features.Employees.components.PayrollRuleSectionShow.no')}
        </td>
      </tr>
    </>
  );
};

const PayrollRuleSectionShow = ({
  employeePayrollRule,
  baseSalaryNames,
  payrollRulesGroups,
  employeeAllowances,
  employeeUnitPrices,
  currentUser,
  isCalculateMotivation
}) => {
  const { t } = useTranslation();
  const hasMotivationAllowance = _.find(payrollRulesGroups, { id: employeePayrollRule.clientPayrollRulesGroupId })?.gotMotivationAllowance;
  return (
    <div>
      <table className={tableStyles.mTablePersonal}>
        <tbody>
          <tr>
            <th>{t('features.EmployeeAttendances.components.DownloadColumnDetail.Salary_rule_group')}：</th>
            <td>{display(getOptionLabel(employeePayrollRule.clientPayrollRulesGroupId)(payrollRulesGroups))}</td>
          </tr>
          <tr>
            <th />
            <td />
          </tr>
        </tbody>
      </table>

      {employeePayrollRule.clientPayrollRulesGroupId && (
        <PayrollRuleInfo payrollRule={payrollRulesGroups.find(p => p.id === employeePayrollRule.clientPayrollRulesGroupId)} />
      )}

      {isRoleTypeAdmin(currentUser.roleType) && (
        <table className={tableStyles.mTablePersonal}>
          <tbody>
            <tr>
              <th />
              <td />
            </tr>
            <tr>
              <th>{getOptionLabel(employeePayrollRule.clientPayrollRulesGroupId)(baseSalaryNames)}</th>
              <td>{currency(numberWithComma(decimalPlace2(employeePayrollRule.baseSalary)))}</td>
            </tr>
            <EmployeeAllowances {...{ employeeAllowances }} payrollRulesGroup={employeePayrollRule.clientPayrollRulesGroup} />
            <EmployeeUnitPrices {...{ employeeUnitPrices }} />
            {hasMotivationAllowance && <EmployeeMotivation {...{ isCalculateMotivation }} />}
          </tbody>
        </table>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  employeePayrollRule: selector(getItem(REDUCER_NAME, state), 'employeePayrollRule') || {},
  baseSalaryNames: getSelectOptions(state, 'baseSalaryNames'),
  employeeAllowances: getSelectOptions(state, 'employeeAllowances'), // Setting payment/deduction items in the employee list
  employeeUnitPrices: getSelectOptions(state, 'employeeUnitPrices'), // Unit price setting
  payrollRulesGroups: getSelectOptions(state, 'payrollRulesGroups'),
  employeeProvidentFunds: getItem(REDUCER_NAME, state).employeeProvidentFunds,
  clientProvidentFunds: getItem(REDUCER_NAME, state).clientProvidentFunds,
  currentUser: getCurrentUserInfo(state) || {},
  isCalculateMotivation: selector(getItem(REDUCER_NAME, state), 'employee').isCalculateMotivation || false
});

export default connect(mapStateToProps)(PayrollRuleSectionShow);
